import React from 'react';
import { TabletButton } from '@campfire/tablet-button';
import { Tabs, Tab, Theme, Box, Typography, Menu, MenuItem, ButtonGroup, Button } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { StringParam, useQueryParams } from 'use-query-params';
import {
  BorderColorOutlined,
  ExpandMore,
  ArrowDropDown,
  EventNote,
  Category,
  Group,
  CreditCard,
} from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { SearchField } from '../../../common/inputs/SearchField';
import { TooltipMenu } from '../../../common/tooltip-menu';
import { RoleIcon } from '../../../common/icons/RoleIconList';
import { useCampfireLazyQuery } from '../../../global/network/useCampfireLazyQuery';
import { useCampfireFetch } from '../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { useSnackbar } from '../../../global/config/useSnackbar';
import { useOrgInfo } from '../../../global/auth/use-org-info/use-org-info';
import { GET_CAKE } from './TasksList/cake-model.gql';
import { TaskListGetCake, TaskListGetCakeVariables } from './TasksList/__generated__/TaskListGetCake';
import { AddTaskStageDialog } from '../admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/dialogs/AddTaskStageDialog';
// TaskStage interface imported but not currently used
// import { TaskStage } from '../admin-console/admin-console-content-pages/admin-console-volunteer-profile/admin-console-volunteer-profile-model';

export enum TasksTabEum {
  DASHBOARD = 'Dashboard',
  TASKS = 'Tasks',
}

const tabs = ['Dashboard', 'Tasks'];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabRoot: {
      color: theme.color.grey.neutralBrand800,
      textTransform: 'none',
      fontWeight: 800,
      fontSize: '20px',
      letterSpacing: '-0.02em',
      minWidth: '90px',
      paddingBottom: '14px',
      opacity: 0.7,
    },
    selected: {
      color: `${theme.color.grey.neutralBrand900} !important`,
      opacity: 1,
    },
    tabsFlexContainer: {
      alignContent: 'space-between',
    },
    root: {
      flex: 1,
    },
  })
);

interface TasksMenuToolbarProps {
  isTasksTab?: boolean;
}

const TasksMenuToolbar = ({ isTasksTab = false }: TasksMenuToolbarProps) => {
  const [{ q, cid }, setQuery] = useQueryParams({
    q: StringParam,
    cid: StringParam,
  });
  const history = useHistory();
  const { theme, isMobile, isMd } = useCampfireTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showTaskStageDialog, setShowTaskStageDialog] = React.useState(false);
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const saveTaskStageMutation = useCampfireFetch({ defer: true });
  const orgInfo = useOrgInfo();

  // Fetch cake details to check if it's the required cake
  const [queryCakeDetails, { data: cakeData }] = useCampfireLazyQuery<TaskListGetCake, TaskListGetCakeVariables>(
    GET_CAKE
  );

  React.useEffect(() => {
    if (cid) {
      queryCakeDetails({
        variables: {
          cakeId: cid,
        },
      });
    }
  }, [cid, queryCakeDetails]);

  const isRequiredCake = cakeData?.vm?.cake?.cakeType === 'required';

  const packageConfig = orgInfo?.packageConfig;
  const packageType = packageConfig?.packageType?.toLowerCase();
  const isStarterPlan = packageType === 'starter';
  const isPlusPlan = packageType === 'plus';
  const isProPlan = packageType === 'pro';

  const getAvailableTaskTypes = () => {
    if (isStarterPlan) {
      return [];
    }
    if (isPlusPlan) {
      return ['task', 'manager', 'referee', 'event', 'stage'];
    }
    if (isProPlan) {
      return ['task', 'manager', 'referee', 'event', 'stripe_payment', 'stage'];
    }
    return ['task', 'manager', 'referee', 'event', 'stripe_payment', 'stage'];
  };

  const requiredCakeOnlyTypes = ['event', 'referee', 'stage'];

  const availableTaskTypes = getAvailableTaskTypes().filter(
    (taskType) => isRequiredCake || !requiredCakeOnlyTypes.includes(taskType)
  );

  const onCreate = (type?: string) => {
    const queryParam = type ? `&type=${type}` : '';
    history.push(`/admin/tasks/task-builder?cid=${cid}${queryParam}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (type: string) => {
    if (type === 'task') {
      onCreate();
    } else if (type === 'manager') {
      onCreate(type);
    } else if (type === 'stage') {
      setShowTaskStageDialog(true);
    } else if (type === 'event') {
      onCreate('event');
    } else if (type === 'referee') {
      onCreate('referee_check');
    } else if (type === 'stripe_payment') {
      onCreate('stripe_product');
    }
    handleMenuClose();
  };

  if (!isTasksTab) {
    return null;
  }

  return (
    <Box display='flex' justifyContent='flex-end' flex={1} alignItems='center'>
      <Box display='flex' alignItems='center'>
        <Box style={{ paddingRight: '10px' }}>
          <SearchField
            value={q || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              e.stopPropagation();
              setQuery({ q: e.target.value });
            }}
          />
        </Box>
        <ButtonGroup
          variant='contained'
          style={{
            backgroundColor: theme.color.secondary.main900,
            borderRadius: '4px',
          }}
        >
          <Button
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              textTransform: 'none',
              padding: !isMobile || !isMd ? '7px 12px' : '7px 7px',
              minWidth: '40px',
              border: 'none',
            }}
            onClick={() => onCreate()}
          >
            <BorderColorOutlined style={{ marginTop: '1px', fontSize: '16px' }} />
            <Typography
              style={{
                marginLeft: '8px',
                fontWeight: 600,
                fontSize: '13px',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                display: isMobile || isMd ? 'none' : 'inherit',
              }}
            >
              Create Task
            </Typography>
          </Button>
          {availableTaskTypes.length > 0 && (
            <TooltipMenu
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              title={
                <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '0.5rem' }}>
                  {availableTaskTypes.includes('task') && (
                    <Button
                      onClick={() => handleMenuItemClick('task')}
                      style={{
                        color: theme.alert.blue.extraLight,
                        display: 'flex',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        alignItems: 'center',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        minWidth: 'auto',
                      }}
                      startIcon={<BorderColorOutlined style={{ fontSize: '16px' }} />}
                    >
                      <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Create Task</Typography>
                    </Button>
                  )}
                  {availableTaskTypes.includes('manager') && (
                    <Button
                      onClick={() => handleMenuItemClick('manager')}
                      style={{
                        color: theme.alert.blue.extraLight,
                        display: 'flex',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        alignItems: 'center',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        minWidth: 'auto',
                      }}
                      startIcon={<RoleIcon iconName='account-check' size={0.8} />}
                    >
                      <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Manager Task</Typography>
                    </Button>
                  )}
                  {availableTaskTypes.includes('referee') && (
                    <Button
                      onClick={() => handleMenuItemClick('referee')}
                      style={{
                        color: theme.alert.blue.extraLight,
                        display: 'flex',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        alignItems: 'center',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        minWidth: 'auto',
                      }}
                      startIcon={<Group fontSize='small' />}
                    >
                      <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Referee Check</Typography>
                    </Button>
                  )}
                  {availableTaskTypes.includes('event') && (
                    <Button
                      onClick={() => handleMenuItemClick('event')}
                      style={{
                        color: theme.alert.blue.extraLight,
                        display: 'flex',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        alignItems: 'center',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        minWidth: 'auto',
                      }}
                      startIcon={<EventNote fontSize='small' />}
                    >
                      <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Event</Typography>
                    </Button>
                  )}
                  {availableTaskTypes.includes('stripe_payment') && (
                    <Button
                      onClick={() => handleMenuItemClick('stripe_payment')}
                      style={{
                        color: theme.alert.blue.extraLight,
                        display: 'flex',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        alignItems: 'center',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        minWidth: 'auto',
                      }}
                      startIcon={<CreditCard fontSize='small' />}
                    >
                      <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Payment Task</Typography>
                    </Button>
                  )}
                  {availableTaskTypes.includes('stage') && (
                    <Button
                      onClick={() => handleMenuItemClick('stage')}
                      style={{
                        color: theme.alert.blue.extraLight,
                        display: 'flex',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        alignItems: 'center',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        minWidth: 'auto',
                      }}
                      startIcon={<Category fontSize='small' />}
                    >
                      <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Task Stage</Typography>
                    </Button>
                  )}
                </Box>
              }
            >
              <Button
                size='small'
                style={{
                  backgroundColor: theme.color.secondary.main900,
                  color: '#ffffff',
                  minWidth: '32px',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
                onClick={handleMenuOpen}
              >
                <ArrowDropDown />
              </Button>
            </TooltipMenu>
          )}
        </ButtonGroup>
      </Box>
      <AddTaskStageDialog
        open={showTaskStageDialog}
        onClose={() => setShowTaskStageDialog(false)}
        onSubmit={async (values) => {
          // Call the SaveTaskStage API endpoint
          try {
            const res = await saveTaskStageMutation.run({
              url: `${apiUrl}/vm/task/stage/save`,
              method: 'post',
              data: {
                name: values.name,
                automateApproval: values.automateApproval,
                order: values.order,
              },
            });

            if (!res.ok) {
              setSnackbar({
                open: true,
                message: res.data?.message || 'Unable to save task stage',
                variant: 'error',
              });
              throw new Error('Failed to save');
            }

            setSnackbar({
              open: true,
              message: 'Task stage created successfully',
              variant: 'success',
            });
            setShowTaskStageDialog(false);

            // Refresh the page to show the new task stage
            window.location.reload();
          } catch (error) {
            setSnackbar({
              open: true,
              message: 'Unable to save task stage',
              variant: 'error',
            });
            throw error;
          }
        }}
        isLoading={saveTaskStageMutation.isLoading}
      />
    </Box>
  );
};

const TasksMenubarMobile = ({
  selectedTab,
  rootPathname,
  hide,
  taskStageRefetch,
}: {
  selectedTab: string;
  rootPathname: string;
  hide?: boolean;
  taskStageRefetch?: (() => Promise<any>) | null;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const history = useHistory();

  const handleOpenDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      display={hide ? 'none' : 'flex'}
      alignItems='center'
      style={{ paddingBottom: '10px', borderBottom: '1px solid #EFEFEF' }}
    >
      <TabletButton
        size='small'
        variant='outlined'
        color='primary'
        aria-controls='task-menu-dropdown-mobile'
        aria-haspopup='true'
        onClick={handleOpenDropdown}
        endIcon={<ExpandMore />}
        style={{
          border: '1px solid #9e9e9e',
          boxSizing: 'border-box',
          borderRadius: '4px',
          padding: '7px 12px',
          color: '#444444',
          marginRight: '8px',
          minWidth: '138px',
          fontSize: '16px',
          fontWeight: 900,
        }}
      >
        {selectedTab}
      </TabletButton>
      <Menu
        id='task-menu-dropdown-mobile'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseDropdown}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {tabs.map((option) => (
          <MenuItem
            key={option}
            onClick={() => {
              if (option === tabs[0]) {
                history.push(`${rootPathname}`);
              } else {
                history.push(`${rootPathname}/${option}`);
              }
              handleCloseDropdown();
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>
      <TasksMenuToolbar isTasksTab={selectedTab === 'Tasks'} taskStageRefetch={taskStageRefetch} />
    </Box>
  );
};

export const TasksMenubar = ({ taskStageRefetch }: { taskStageRefetch?: (() => Promise<any>) | null }) => {
  const classes = useStyles();
  const rootPathname = '/admin/tasks';

  const location = useLocation();
  const history = useHistory();

  const { isMobile, theme } = useCampfireTheme();

  const selectedTab = location.pathname.split('/')[3] || tabs[0];

  const handleChange = (_unused: any, newValue: TasksTabEum) => {
    if (newValue === tabs[0]) {
      history.push(`${rootPathname}`);
    } else {
      history.push(`${rootPathname}/${newValue}`);
    }
  };

  if (isMobile) {
    return (
      <TasksMenubarMobile selectedTab={selectedTab} rootPathname={rootPathname} taskStageRefetch={taskStageRefetch} />
    );
  }

  return (
    <Tabs
      value={selectedTab || TasksTabEum.DASHBOARD}
      onChange={handleChange}
      indicatorColor='primary'
      textColor='primary'
      aria-label='icon tabs example'
      classes={{
        flexContainer: classes.tabsFlexContainer,
      }}
      style={{
        borderBottom: `1px solid ${theme.color.grey.border}`,
        backgroundColor: 'white',
      }}
    >
      <Tab
        label='Dashboard'
        classes={{ selected: classes.selected, root: classes.tabRoot }}
        value={TasksTabEum.DASHBOARD}
        disableRipple
        disableTouchRipple
      />
      <Tab label='Tasks' classes={{ selected: classes.selected, root: classes.tabRoot }} value={TasksTabEum.TASKS} />
      <TasksMenuToolbar isTasksTab={selectedTab === TasksTabEum.TASKS} taskStageRefetch={taskStageRefetch} />
    </Tabs>
  );
};
